import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Notification = Database['public']['Tables']['notifications']['Row'];
type NotificationPreferences = Database['public']['Tables']['notification_preferences']['Row'];

export const notificationService = {
  /**
   * Get all notifications for the current user
   */
  async getAll(options?: { read?: boolean; limit?: number }) {
    const query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (options?.read !== undefined) {
      query.eq('read', options.read);
    }

    if (options?.limit) {
      query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const { data, error } = await supabase.rpc('get_unread_notification_count');
    
    if (error) throw error;
    return data || 0;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string) {
    const { error } = await supabase.rpc('mark_notification_read', {
      _notification_id: notificationId,
    });

    if (error) throw error;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    const { error } = await supabase.rpc('mark_all_notifications_read');

    if (error) throw error;
  },

  /**
   * Delete a notification
   */
  async delete(notificationId: string) {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
  },

  /**
   * Get user notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      // If preferences don't exist, create default ones
      if (error.code === 'PGRST116') {
        return await this.createDefaultPreferences(user.id);
      }
      throw error;
    }

    return data;
  },

  /**
   * Create default preferences for a user
   */
  async createDefaultPreferences(userId: string): Promise<NotificationPreferences> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .insert({
        user_id: userId,
        email_enabled: true,
        email_frequency: 'immediate',
        notification_types: {
          order_ready: true,
          order_submitted: true,
          order_delivered: true,
          low_inventory: true,
          user_joined: true,
          menu_deadline: true,
          system: true,
        },
        quiet_hours_enabled: false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update user notification preferences
   */
  async updatePreferences(preferences: Partial<NotificationPreferences>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('notification_preferences')
      .update(preferences)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  /**
   * Subscribe to real-time notifications
   */
  subscribeToNotifications(
    callback: (notification: Notification) => void
  ): (() => void) {
    // Generate unique channel name to support React StrictMode double-mounting
    const channelName = `notifications-changes-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Trigger email notification (calls edge function)
   */
  async sendEmail(notificationId: string) {
    const { data, error } = await supabase.functions.invoke('send-notification-email', {
      body: { notificationId },
    });

    if (error) throw error;
    return data;
  },
};
